import { useEffect, useRef, useState } from 'react';

const preferenceKey = 'admin.paymentNotifications';
const supported = () => typeof window.Notification === 'function' && window.isSecureContext !== false;
const savedPreference = () => {
    try { return localStorage.getItem(preferenceKey) === 'enabled'; } catch { return false; }
};
const savePreference = enabled => {
    try { localStorage.setItem(preferenceKey, enabled ? 'enabled' : 'disabled'); } catch { /* Storage may be unavailable. */ }
};

export default function usePaymentNotifications(data, onOpen) {
    const [enabled, setEnabled] = useState(() => supported() && Notification.permission === 'granted' && savedPreference());
    const [message, setMessage] = useState('');
    const [requesting, setRequesting] = useState(false);
    const previous = useRef({ time: null, statuses: new Map() });
    const open = useRef(onOpen);
    open.current = onOpen;

    useEffect(() => {
        if (!data) return;
        const rows = data.activity || [...(data.recent || []), ...(data.today?.recent || [])];
        const snapshot = previous.current;
        const seen = new Set();
        for (const row of [...rows].reverse()) {
            const id = String(row._id || row.orderId);
            if (seen.has(id)) continue;
            seen.add(id);
            const oldStatus = snapshot.statuses.get(id);
            const isNewUpdate = new Date(row.updatedAt).getTime() > snapshot.time;
            const changed = oldStatus ? oldStatus !== row.status : isNewUpdate;
            snapshot.statuses.set(id, row.status);
            // First load establishes a baseline, without alerting on history.
            if (!snapshot.time || !changed || !enabled || !supported() || Notification.permission !== 'granted') continue;
            try {
                const purpose = row.eventName || row.planName || row.method?.replace(/_/g, ' ') || 'Payment';
                const amount = row.paid_amount == null ? '' : ` · ${row.currency || ''} ${Number(row.paid_amount).toFixed(2)}`;
                const notification = new Notification(`Payment ${row.status}`, {
                    body: `${row.orderId} · ${purpose}${amount}`,
                    tag: `payment-${id}-${row.status}`,
                });
                notification.onclick = () => {
                    window.focus();
                    open.current(row.status === 'pending' ? 'live' : 'today');
                    notification.close();
                };
            } catch {
                setMessage('This browser could not display notifications. Check browser and system notification settings.');
            }
        }
        snapshot.time = new Date(data.generatedAt).getTime();
        // Bound memory during long dashboard sessions.
        if (snapshot.statuses.size > 1000) snapshot.statuses = new Map(rows.map(row => [String(row._id || row.orderId), row.status]));
    }, [data, enabled]);

    const toggle = async () => {
        setMessage('');
        if (enabled) { setEnabled(false); savePreference(false); return; }
        if (!supported()) { setMessage('Browser notifications require a supported browser on HTTPS or localhost.'); return; }
        setRequesting(true);
        try {
            const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
            const allowed = permission === 'granted';
            setEnabled(allowed);
            savePreference(allowed);
            if (!allowed) setMessage(permission === 'denied'
                ? 'Notifications are blocked. Allow notifications for this site in your browser settings, then try again.'
                : 'Notification permission was not granted. You can enable it when ready.');
        } catch {
            setMessage('Could not request notification permission. Check your browser settings.');
        } finally {
            setRequesting(false);
        }
    };
    return { enabled, message, requesting, toggle };
}
